import * as Sentry from '@sentry/tanstackstart-react';
import { useEffect } from 'react';

import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';

/**
 * SentryContextProvider automatically enriches Sentry with user and organization context.
 * This component should be placed high in the component tree to ensure all errors
 * have access to the enriched context.
 */
export function SentryContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useOneRosterAuth();

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      Sentry.setContext('user', {
        id: auth.user.sourcedId,
        email: auth.user.email,
        isAdmin: auth.isAdmin,
        isSysAdmin: auth.isSysAdmin,
        isAide: auth.isAide,
        isParent: auth.isParent,
        isImpersonated: auth.isImpersonated,
      });

      if (auth.user.primaryOrg) {
        Sentry.setContext('organization', {
          id: auth.user.primaryOrg.sourcedId,
        });
      }

      Sentry.setTags({
        userId: auth.user.sourcedId,
        organizationId: auth.user.primaryOrg?.sourcedId,
      });
    } else {
      Sentry.setContext('user', null);
      Sentry.setContext('organization', null);
      Sentry.setTags({
        userId: null,
        organizationId: null,
      });
    }
  }, [
    auth.isAuthenticated,
    auth.user,
    auth.isAdmin,
    auth.isParent,
    auth.isAide,
    auth.isImpersonated,
    // Include specific user fields that might change
    auth.user?.sourcedId,
    auth.user?.email,
    auth.user?.primaryOrg?.sourcedId,
  ]);

  return <>{children}</>;
}
